// Qué dice y qué ofrece la pantalla de entrada según cómo se puede entrar a esta tienda (documento 20, decisiones D48 y
// D51): correo y contraseña siempre; Google, Apple y Facebook si el servidor los tiene; el celular con código solo si la
// empresa lo prendió. Para alguien que nunca pidió tiene que quedar claro cómo crea su cuenta.
import { CLAVE_MINIMA } from '../compartido';
import type { Proveedor } from './google';
import type { MarcaPublica } from './tipos';

type Acceso = Partial<MarcaPublica['acceso']> | undefined;

/** La tienda acepta cuentas nuevas (abierto o con aprobación). */
export const aceptaRegistros = (acceso: Acceso): boolean => !!acceso?.registro && acceso.registro !== 'cerrado';

/** Los proveedores que tiene el servidor, en el orden de los botones. */
export function proveedoresDe(acceso: Acceso): Proveedor[] {
  return (['google', 'apple', 'facebook'] as const).filter((p) => !!acceso?.[p]);
}

/** Entrar con el celular y un código (`acceso.celular`): una opción aparte, abajo. */
export const conCelular = (acceso: Acceso): boolean => !!(acceso?.celular ?? acceso?.codigo);

export interface TextosEntrada {
  titulo: string;
  subtitulo: string;
}

export function textosEntrada(acceso: Acceso): TextosEntrada {
  return {
    titulo: 'Entrá a tu cuenta',
    subtitulo: aceptaRegistros(acceso)
      ? 'Con tu correo y tu contraseña. Si es tu primera vez, creá tu cuenta en un minuto.'
      : 'Con tu correo y tu contraseña.',
  };
}

/** Después de crear la cuenta (o de entrar por primera vez): faltan los datos del negocio. */
export const TEXTO_PRIMERA_VEZ = {
  titulo: 'Es tu primera vez',
  subtitulo: 'Completá los datos de tu negocio para empezar a pedir. Lo hacés una sola vez.',
} as const;

/** El correo, como lo compara la API: sin espacios y en minúsculas. */
export const correoLimpio = (v: string): string => v.trim().toLowerCase();

export function errorCorreo(v: string): string | null {
  const c = correoLimpio(v);
  if (!c) return 'Escribí tu correo.';
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c) ? null : 'Revisá el correo: por ejemplo nombre@gmail.com';
}

/** Una contraseña nueva: 8 caracteres o más, sin otras reglas. */
export function errorClaveNueva(v: string): string | null {
  if (!v) return 'Elegí una contraseña.';
  return v.length < CLAVE_MINIMA ? `Tiene que tener ${CLAVE_MINIMA} caracteres o más.` : null;
}

/** El enlace para elegir la contraseña (`/clave#t=…`): lo lee y lo saca de la dirección. */
export function tomarTokenClave(): string | null {
  if (typeof location === 'undefined') return null;
  const m = /(?:^#|&)t=([A-Za-z0-9_-]{30,80})/.exec(location.hash);
  if (m) history.replaceState(history.state, '', `${location.pathname}${location.search}`);
  return m?.[1] ?? null;
}
